import React from 'react';
import Table, { TableColumn } from '../../components/common/Table';
import { ScoreDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import Badge from '../common/Badge';
import FileIcons from '../common/FileIcons';
import { getBadgeColors } from '../../utils/displayUtils';
import { renderCaliberBadge } from '../../utils/firearmDisplayUtils';
import { getRoles } from '../../utils/tokenUtils';
import createScoreActions from '../actionMenus/ScoreActions';
import FeedbackBubble from '../common/FeedbackBubble';
import { useScoreApi } from '../../hooks/useScoreApi';
import { ScoreStatusConstants } from '../../constants/ScoreStatusConstants';
import MemberNoteIcons from '../common/MemberNoteIcons';
import { FirearmCaliberConstants } from '../../constants/FirearmCaliberConstants';
import { FirearmMakeConstants } from '../../constants/FirearmMakeConstants';
import { ActivityTypeConstants } from '../../constants/ActivityTypeConstants';
import ReviewPanel from '../reviewPanels/ReviewPanel';

interface ScoreTableProps {
  data: ScoreDTO[];
  showCourseColumn?: boolean;
  handleViewMemberDetails: (row: ScoreDTO) => void;
  handleEdit: (row: ScoreDTO) => void;
  handleDelete: (row: ScoreDTO) => void;
  handleSendEmail: (row: ScoreDTO) => void;
  handleViewAllEmails: (row: ScoreDTO) => void;
  handleViewRecordEmails: (row: ScoreDTO) => void;
  handleRestore: (row: ScoreDTO) => Promise<void>;
  handleApprove: (row: ScoreDTO) => void;
  handleChangeStatus: (row: ScoreDTO) => void;
  handleImpersonateMemberLogin: (row: ScoreDTO) => void;
  handleAddNote: (row: ScoreDTO) => void;
  handleViewNoteHistory: (row: ScoreDTO) => void;
  handleMemberClick?: (memberId: string) => void;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  onSort?: (sortKey: string) => void;
  selectedIds?: Set<string>;
  onSelectRow?: (id: string, checked: boolean) => void;
  onSelectAll?: (checked: boolean) => void;
}

const ScoreTable: React.FC<ScoreTableProps> = ({
  data,
  showCourseColumn = false,
  handleViewMemberDetails,
  handleEdit,
  handleDelete,
  handleSendEmail,
  handleViewAllEmails,
  handleViewRecordEmails,
  handleRestore,
  handleApprove,
  handleChangeStatus,
  handleImpersonateMemberLogin,
  handleAddNote,
  handleViewNoteHistory,
  handleMemberClick,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
  sortBy,
  sortDescending,
  onSort,
  selectedIds = new Set(),
  onSelectRow,
  onSelectAll,
}) => {
  const roles = getRoles();
  const isAdmin = roles?.includes('Admin');
  const { getFeedback } = useScoreApi();

  const allCurrentPageIds = data.map(row => row.id).filter((id): id is string => !!id);
  const allSelected = allCurrentPageIds.length > 0 && allCurrentPageIds.every(id => selectedIds.has(id));
  const someSelected = allCurrentPageIds.some(id => selectedIds.has(id)) && !allSelected;

  // Check if status is locked (Approved, Approved with Feedback, or Declined)
  const isStatusLocked = (row: ScoreDTO): boolean => {
    const lockedStatuses = [
      ScoreStatusConstants.APPROVED.toLowerCase(),
      ScoreStatusConstants.APPROVED_WITH_FEEDBACK.toLowerCase(),
      ScoreStatusConstants.OUTSTANDING.toLowerCase(),
      ScoreStatusConstants.DECLINED.toLowerCase(),
    ];
    return lockedStatuses.includes(row.scoreStatusId?.toLowerCase() || '');
  };

  const columns: TableColumn[] = [
    ...(onSelectRow && onSelectAll
      ? [
        {
          label: (
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={(e) => onSelectAll?.(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
            />
          ),
          key: 'checkbox',
          render: (row: ScoreDTO) => (
            <input
              type="checkbox"
              checked={selectedIds.has(row.id ?? '')}
              onChange={(e) => onSelectRow?.(row.id ?? '', e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
            />
          ),
        },
      ]
      : []),

    {
      label: 'Type',
      key: 'scoreTypeId',
      sortable: true,
      sortKey: 'ScoreTypeTitle',
      showInReview: true,
      render: (row: ScoreDTO) => <span>{row.scoreType?.title}</span>,
    },

    ...(isAdmin
      ? [
        {
          label: 'Member',
          key: 'member',
          sortable: true,
          sortKey: 'FirstName',
          showInReview: true,
          render: (row: ScoreDTO) => (
            <div
              onClick={() => handleMemberClick?.(row.member?.id ?? '')}
              className="flex flex-col gap-1 cursor-pointer"
            >
              <span>
                {row.member?.firstName} {row.member?.lastName}
              </span>
              <span>{row.member?.idPassportNumber ?? ''}</span>
              <MemberNoteIcons member={row.member} tableContext="scores" recordId={row.id} />
            </div>
          ),
        },
        {
          label: 'Membership',
          key: 'membership',
          sortable: true,
          sortKey: 'MemberTypeTitle',
          showInReview: true,
          render: (row: ScoreDTO) => <span>{row.member?.memberType?.title}</span>,
        },
      ]
      : []),

    ...(isAdmin
      ? [
        {
          label: 'FCA',
          key: 'memberCategory',
          sortable: true,
          sortKey: 'MemberCategoryTitle',
          showInReview: true,
          render: (row: ScoreDTO) => <span>{row.member?.memberCategory?.titleShort}</span>,
        },
      ]
      : []),
    {
      label: 'Date',
      key: 'dateActivity',
      sortable: true,
      sortKey: 'DateActivity',
      showInReview: true,
      format: (value: Date) => convertToUserTime(value?.toString() ?? '', true),
    },

    ...(isAdmin
      ? [
        {
          label: 'Firearm Type',
          key: 'firearmTypeId',
          sortable: true,
          sortKey: 'FirearmTypeTitle',
          showInReview: true,
          render: (row: ScoreDTO) => (
            <div className="flex flex-col gap-1">
              <span>{row.memberFirearm?.firearmType?.title}</span>
            </div>
          ),
        },
        {
          label: 'Specific Firearm',
          key: 'firearmSpecific',
          sortable: true,
          sortKey: 'FirearmSpecificTitle',
          showInReview: true,
          render: (row: ScoreDTO) => <span>{row.memberFirearm?.firearmSpecific?.title}</span>,
        },
      ]
      : []),

    {
      label: isAdmin ? 'Location' : 'Shooting Range',
      key: 'location',
      sortable: true,
      sortKey: 'Location',
      showInReview: true,
    },

    {
      label: 'Exercise',
      key: 'exerciseTypeId',
      sortable: true,
      sortKey: 'ExerciseTypeTitle',
      showInReview: true,
      render: (row: ScoreDTO) => <span>{row.exerciseType?.title}</span>,
    },

    ...(isAdmin
      ? [
        {
          label: 'Specific',
          key: 'specific',
          showInReview: true,
          render: (row: ScoreDTO) => <span>{row.exerciseSpecific?.title}</span>,
        },
        {
          label: 'Calibre',
          key: 'firearmCalibreId',
          sortable: true,
          sortKey: 'CaliberTitle',
          showInReview: true,
          render: (row: ScoreDTO) => (
            <>
              {renderCaliberBadge(
                row.memberFirearm?.firearmCaliberId === FirearmCaliberConstants.NOT_ON_LIST ? row.memberFirearm?.caliberOther : row.memberFirearm?.caliber?.title,
                row.memberFirearm?.firearmSpecificId
              ) || <span>-</span>}
            </>
          ),
        },
      ]
      : []),

    ...isAdmin && showCourseColumn
      ? [
        {
          label: 'Course',
          key: 'activityType',
          render: (row: ScoreDTO) => {
            const hunterCourse = row.activityTypeId === ActivityTypeConstants.HUNT_RELATED ? "DH" : null;
            const sportCourse = row.activityTypeId === ActivityTypeConstants.SPORT_SHOOTING ? "DS" : null;
            return (
              <span>
                {hunterCourse || sportCourse || '-'}
              </span>
            );
          }
        }
      ]
      : [],

    ...(!isAdmin
      ? [
        {
          label: 'Firearm',
          key: 'memberFirearmId',
          render: (row: ScoreDTO) => {
            const makeTitle = row.memberFirearm?.firearmMakeId === FirearmMakeConstants.NOT_ON_LIST ? row.memberFirearm?.firearmMakeOther : row.memberFirearm?.firearmMake?.title ?? 'Unknown';
            const caliberTitle = row.memberFirearm?.firearmCaliberId === FirearmCaliberConstants.NOT_ON_LIST ? row.memberFirearm?.caliberOther : row.memberFirearm?.caliber?.title ?? 'Unknown';
            return <span>{makeTitle} - {caliberTitle}</span>;
          },
        },

        {
          label: 'Specific Exercise',
          key: 'exerciseSpecificId',
          render: (row: ScoreDTO) => <span>{row.exerciseSpecific?.title}</span>,
        },
      ]
      : []),
    {
      label: 'Documents',
      key: '',
      render: (row: ScoreDTO) => (
        <FileIcons
          files={[
            ...(row.scoreTargetFiles?.map((file) => ({
              id: file.fileRecordId,
              label: '',
            })) || []),
            {
              id: row.fileAdditionalId,
              label: 'Additional Document',
            },
          ]}
          renderSidePanel={isAdmin ? () => <ReviewPanel row={row} columns={columns} /> : undefined}
        />
      ),
    },
    {
      label: 'Mark/Score',
      key: 'score',
      sortable: true,
      sortKey: 'GrandTotalScore',
      showInReview: true,
      render: (row: ScoreDTO) => {
        // GrandTotalMark is only populated when a template section has shouldCalculateMark,
        // so a positive mark means this is a mark-based (multi-target) score.
        const isMarkBased = (row.grandTotalMark ?? 0) > 0;
        const primary = isMarkBased ? row.grandTotalMark?.toFixed(2) : row.grandTotalScore;
        return (
          <span>{`${primary ?? ''} (${row.grandTotalPercentage ?? ''}%)`}</span>
        );
      },
    },

    ...(isAdmin
      ? [{
        label: 'FB',
        key: 'feedback',
        render: (row: ScoreDTO) => (
          <FeedbackBubble
            hasFeedback={!!row.feedbackFileId}
            fetchFeedback={() => getFeedback(row.id!)}
          />
        ),
      },
      ]
      : []),

    {
      label: 'Status',
      key: 'scoreStatusId',
      sortable: true,
      sortKey: 'ScoreStatusTitle',
      showInReview: true,
      render: (row: ScoreDTO) => {
        if (row.isDeleted) {
          return <Badge label="Deleted" variant="error" />;
        }
        const base = row.scoreStatus?.colourGrading?.colour ?? '#ffffffff';
        const { bg, text } = getBadgeColors(base, 0.18, 0.4);
        return base ? (
          <Badge
            label={row.scoreStatus?.title || 'Unknown'}
            variant="custom"
            customBgColor={bg}
            customTextColor={text}
          />
        ) : (
          <span>{row.scoreStatus?.title || 'Unknown'}</span>
        );
      },
    },

    ...(isAdmin
      ? [
        {
          label: 'Updated',
          key: 'updatedUtc',
          sortable: true,
          sortKey: 'UpdatedUtc',
          format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
        },
      ]
      : []),

    {
      label: isAdmin ? 'Created' : 'Submitted',
      key: 'createdUtc',
      sortable: true,
      sortKey: 'CreatedUtc',
      format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
    },
  ];

  return (
    <Table
      columns={columns}
      data={data}
      actions={{
        ...(!isAdmin && {
          extra: (row) => {
            // For non-admin users, show edit action conditionally
            if (isStatusLocked(row)) {
              return [];
            }
            return [
              {
                label: 'Edit Score',
                onClick: () => handleEdit(row),
              },
            ];
          },
        }),
        ...(isAdmin && {
          onEdit: handleEdit,
          extra: (row) =>
            createScoreActions({
              onViewMemberDetails: handleViewMemberDetails,
              onView: handleEdit,
              onEdit: handleEdit,
              onDelete: handleDelete,
              onSendEmail: handleSendEmail,
              onViewAllEmails: handleViewAllEmails,
              onViewRecordEmails: handleViewRecordEmails,
              onRestore: handleRestore,
              onApprove: handleApprove,
              onChangeStatus: handleChangeStatus,
              onImpersonateMemberLogin: handleImpersonateMemberLogin,
              onAddNote: handleAddNote,
              onViewNoteHistory: handleViewNoteHistory,
            })(row),
        }),
      }}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
      sortBy={sortBy}
      sortDescending={sortDescending}
      onSort={onSort}
    />
  );
};

export default ScoreTable;
